import React, { useCallback, useContext, useState } from "react"
import { css, cx } from "@emotion/css"
import { formatFloatingPointNumber, parseNumber } from "utils/number-format"

import Autocomplete from "@material-ui/lab/Autocomplete"
import Button from "ui/buttons/Button"
import { ButtonGroup } from "components/buttons/ButtonGroup"
import { CurrencyContext } from "../../ExchangeRates"
import DropdownIcon from "@material-ui/icons/KeyboardArrowDown"
import { ExchangeRatePayload } from "models/exchangeRates"
import { TextInput } from "components/inputs/TextInput"
import { currency_options } from "utils/currencies"
import { useI18n } from "providers/I18n"
import { useRifm } from "rifm"

const formatter = formatFloatingPointNumber(10, 2)
interface AddNewExchangeRowProps {
  onClose: () => void
  onCreate: (newRow: ExchangeRatePayload) => Promise<void>
  clientId: string
}

const AddNewExchangeRow: React.FC<AddNewExchangeRowProps> = ({
  onClose,
  onCreate,
  clientId,
}) => {
  const { getFilteredCurrencyOptions } = useContext(CurrencyContext)
  const { t } = useI18n()

  const baseCurrencyOptions = currency_options
  const [baseCurrency, setBaseCurrency] = useState("")
  const [currency, setCurrency] = useState("")
  const [exchangeRate, setExchangeRate] = useState("")
  const [error, setError] = useState<string | undefined>(undefined)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleCreate = useCallback(async () => {
    setError(undefined)
    if (baseCurrency && currency && exchangeRate) {
      const newRow = {
        clientId,
        sourceCurrency: baseCurrency,
        targetCurrency: currency,
        exchangeRate: parseFloat(exchangeRate),
      }
      try {
        setIsSubmitting(true)
        await onCreate(newRow)
        setError(undefined)
      } catch (err: any) {
        if (err.info.status === 400) {
          setError(t("admin.exchange_rate.error.already_exits"))
        } else {
          setError(t("admin.exchange_rate.error.unknown"))
        }
      } finally {
        setIsSubmitting(false)
      }
    }
  }, [baseCurrency, currency, exchangeRate, onCreate, clientId, t])

  const rifm = useRifm({
    value: !!exchangeRate ? `${exchangeRate}` : "",
    accept: /[\d.]/g,
    onChange: (v) => setExchangeRate(parseNumber(v)),
    format: (v) => (!!v ? formatter(v) : ""),
  })

  const filteredCurrencyOptions = getFilteredCurrencyOptions(baseCurrency)

  const isAddButtonDisabled =
    !baseCurrency ||
    !currency ||
    !exchangeRate ||
    parseFloat(exchangeRate) <= 0 ||
    isSubmitting

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        <Autocomplete
          options={baseCurrencyOptions}
          getOptionLabel={(option) => option.label}
          value={
            baseCurrencyOptions.find((opt) => opt.value === baseCurrency) ||
            null
          }
          onChange={(_, option) => setBaseCurrency(option?.value || "")}
          className={styles.input}
          popupIcon={<DropdownIcon />}
          renderInput={(params) => (
            <TextInput
              {...params}
              label={t("admin.exchange_rate.base_currency")}
              placeholder={t("admin.exchange_rate.select_base_currency")}
              size="medium"
              margin="normal"
              InputProps={{
                ...params.InputProps,
                className: cx("BodyBody-2BlackRegular", styles.selectInput),
                endAdornment: (
                  <React.Fragment>
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
              errorMessage={error}
              error={!!error}
            />
          )}
        />
        <Autocomplete
          options={filteredCurrencyOptions}
          getOptionLabel={(option) => option.label}
          value={
            filteredCurrencyOptions.find((opt) => opt.value === currency) ||
            null
          }
          onChange={(_, option) => setCurrency(option?.value || "")}
          renderInput={(params) => (
            <TextInput
              {...params}
              label={t("admin.exchange_rate.currency")}
              placeholder={t("admin.exchange_rate.select_currency")}
              size="medium"
              margin="normal"
              InputProps={{
                ...params.InputProps,
                className: cx("BodyBody-2BlackRegular", styles.selectInput),
                endAdornment: (
                  <React.Fragment>
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
              errorMessage={error}
              error={!!error}
            />
          )}
        />
        <TextInput
          label={t("admin.exchange_rate.exchange_rate")}
          placeholder={t("admin.exchange_rate.enter.exchange_rate")}
          value={rifm.value}
          onChange={rifm.onChange}
          size="medium"
          color="primary"
          margin="normal"
          className={cx("BodyBody-2BlackRegular", styles.input)}
        />
      </div>
      <ButtonGroup className={styles.actions}>
        <Button variant="outlined" color="primary" onClick={onClose}>
          {t("admin.exchange_rate.cancel")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreate}
          disabled={isAddButtonDisabled}
        >
          {t("admin.exchange_rate.add")}
        </Button>
      </ButtonGroup>
    </div>
  )
}

export default AddNewExchangeRow

const styles = {
  wrapper: css`
    border-radius: 4px;
    box-shadow: 1px 6px 10px 0 var(--disabled-grey);
    border: solid 1px var(--lighter-grey);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--spacing-s);
    margin-bottom: var(--spacing-m);
  `,
  content: css`
    display: flex;
    align-items: center;
  `,
  selectInput: css`
    width: 250px;
    padding-right: var(--spacing-xxl-4);
    margin-right: 12px;
    .MuiFormControl-marginNormal {
      margin: 0;
    }
    .MuiAutocomplete-endAdornment {
      right: 9px;
    }
  `,
  input: css`
    width: 250px;
    margin-right: 12px;
  `,
  span: css`
    margin-right: var(--spacing-xs);
    margin-left: var(--spacing-m);
  `,
  textButton: css`
    --btn-padding: 0;
    --btn-min-width: 0;
    --btn-line-height: 1;
    --btn-color: inherit;
    --btn-font-weight: normal;
    --btn-bg-hover: transparent;
    --btn-bg-active: transparent;
    --btn-border-color-hover: transparent;
    --btn-bg-focus-ring-color: transparent;
    width: fit-content;
    &:focus {
      box-shadow: none;
    }
  `,
  actions: css`
    display: flex;
  `,
}
